import React from 'react'
import {injectIntl} from 'react-intl'
import styled from 'styled-components'
import FormControl from '@mui/material/FormControl'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import {getDataExportOptions} from 'utils/utils'
import {dataExportSourceType} from 'utils/helpers'
import {fieldtype} from 'utils/enums/fieldtype'
import Field from './Field'

const Title = styled.div`
    font-size: 20px;
    display: flex;
    justify-content: space-between;
    padding-bottom: 20px;
`

const values = {
    empty: 0,
    custom: dataExportSourceType.Custom,
    field: dataExportSourceType.ExternalField,
    link: dataExportSourceType.LinkToSource
}

class DataExport extends React.Component {
    constructor(props) {
        super(props)

        const destinationId = props.template ? props.template.destinationId : props.entities.length ? props.entities[0].id : ''
        this.options = getDataExportOptions(props.entity, destinationId)

        this.state = {
            name: props.template ? props.template.name : '',
            destinationId,
            mapping: props.template ? [...props.template.mapping] : this.getMapping(this.options)
        }
    }

    componentDidMount() {
        if (this.options.length && this.props.template) {
            this.options.forEach(item => {
                const mapping = this.state.mapping.find(field => field.fieldId === item.field.id)

                if (mapping && mapping.type === values.custom) {
                    if (item.field.uiType === fieldtype.linkedfield) {
                        this.props.fetchLinkedOptions(item.field.id)
                    }
                }
            })
        }
    }

    getMapping = (fields) => {
        const mapping = []

        if (fields) {
            fields.forEach(field => {
                if (field.options.length) {
                    mapping.push({
                        fieldId: field.field.id,
                        type: dataExportSourceType.ExternalField,
                        customValue: null,
                        sourceFieldId: field.options[0].id
                    })
                }
            })
        }

        return mapping
    }

    handleChange = (e) => {
        if (e.target.name === 'destinationId') {
            this.options = getDataExportOptions(this.props.entity, e.target.value)
            const mapping = this.getMapping(this.options)

            this.setState({
                [e.target.name]: e.target.value,
                mapping
            })
        } else {
            this.setState({
                [e.target.name]: e.target.value
            })
        }
    }

    handleDelete = () => {
        this.props.deleteTemplate(this.props.template.id)
        this.props.handleClose()
    }

    handleConfirm = () => {
        if (this.props.template) {
            this.props.exportData({
                id: this.props.template.id,
                entityId: this.props.entity,
                name: this.state.name,
                destinationId: this.state.destinationId,
                mapping: this.state.mapping
            })
        } else {
            this.props.exportData({
                entityId: this.props.entity,
                name: this.state.name,
                destinationId: this.state.destinationId,
                mapping: this.state.mapping
            })
        }
    }

    handleChangeType = (value, fieldId) => {
        const mapping = [...this.state.mapping]
        const obj = {...mapping.find(item => item.fieldId === fieldId)}
        const index = mapping.findIndex(item => item.fieldId === fieldId)
        const options = this.options.find(item => item.field.id === fieldId).options

        if (index !== -1) {
            if (value === obj.type) {
                if (options.length) {
                    obj.type = values.field
                    obj.customValue = null
                    obj.sourceFieldId = options[0].id

                    mapping.splice(index, 1, obj)
                } else {
                    mapping.splice(index, 1)
                }
            } else if (value === values.empty) {
                mapping.splice(index, 1)
            } else {
                obj.type = value
                obj.sourceFieldId = null
                obj.customValue = null
                mapping.splice(index, 1, obj)
            }
        } else {
            if (options.length || value) {
                obj.fieldId = fieldId
                obj.type = value ? value : values.field
                obj.customValue = null
                obj.sourceFieldId = value ? null : options[0].id
                mapping.push(obj)
            }
        }

        this.setState({ mapping })
    }

    handleChangeValue = (value, fieldId) => {
        const mapping = [...this.state.mapping]
        const obj = {...mapping.find(item => item.fieldId === fieldId)}
        const index = mapping.findIndex(item => item.fieldId === fieldId)

        if (obj.type === values.custom) {
            obj.customValue = value
        } else {
            obj.sourceFieldId = value
        }

        mapping.splice(index, 1, obj)

        this.setState({ mapping })
    }

    render() {
        const { intl, entities, permissions, sending, entity } = this.props
        const source = entities.find(obj => obj.id === entity)
        const destination = entities.find(obj => obj.id === this.state.destinationId)
        const validation = !destination || this.state.name.replace(/\s/g, '') === ''

        return (
            <React.Fragment>
                <DialogTitle>{intl.formatMessage({id: "sidebar.export"})}</DialogTitle>
                <DialogContent>
                    <TextField variant="standard"
                        value={this.state.name}
                        onChange={this.handleChange}
                        label={intl.formatMessage({id: "sidebar.template_name"})}
                        disabled={!permissions.Admin}
                        InputLabelProps={{
                            shrink: true
                        }}
                        inputProps={{
                            maxLength: global.NAME_MAX_LENGTH,
                            name: 'name'
                        }}
						sx={{
							width: '100%',
							marginBottom: '20px'
						}}
                    />
                    <FormControl disabled={!permissions.Admin} variant="standard" sx={{width: '100%',  marginBottom: '20px'}}>
                        <InputLabel shrink={true}>
                            {intl.formatMessage({id: "sidebar.destination_table"})}
                        </InputLabel>
                        <Select
                            value={this.state.destinationId}
                            onChange={this.handleChange}
                            inputProps={{
                                name: 'destinationId'
                            }}
                        >
                            {entities.filter(obj => obj.id !== entity).map(item =>
                                <MenuItem key={item.id} value={item.id}>
                                    {item.singular}
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <div>
                        {destination && 
                            <Title>
                                <div>{source.singular}</div>
                                <div>{destination.singular}</div>
                            </Title>
                        }
                        {this.options.map(item =>
                            <Field key={item.field.id}
                                data={item}
                                values={values}
                                mapping={this.state.mapping.find(obj => obj.fieldId === item.field.id)}
                                handleChangeType={this.handleChangeType}
                                handleChangeValue={this.handleChangeValue}
                                linkedOptions={this.props.linkedOptions[item.field.id]}
                            />
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.props.handleClose} color="primary">
                        {intl.formatMessage({id: "app.confirm_cancel"})}
                    </Button>
                    {this.props.template && permissions.Admin &&
                        <Button variant="contained"
                            onClick={this.handleDelete}
                            color="secondary"
                        >
                            {intl.formatMessage({id: "app.confirm_delete"})}
                        </Button>
                    }
                    {sending && permissions.Admin &&
                        <Button variant="contained"
                            disabled={validation}
                            onClick={this.handleConfirm}
                            color="primary"
                        >
                            {intl.formatMessage({id: "app.confirm_save_export"})}
                        </Button>
                    }
                    {!sending && permissions.Admin &&
                        <Button variant="contained"
                            disabled={validation}
                            onClick={this.handleConfirm}
                            color="primary"
                        >
                            {intl.formatMessage({id: "app.confirm_save"})}
                        </Button>
                    }
                    {sending && !permissions.Admin &&
                        <Button variant="contained"
                            disabled={validation}
                            onClick={this.handleConfirm}
                            color="primary"
                        >
                            {intl.formatMessage({id: "app.confirm_export"})}
                        </Button>
                    }
                </DialogActions>
            </React.Fragment>
        )
    }
}

export default injectIntl(DataExport)
